import i18n from '@/utils/i18n';
import { ArrowDirection } from '@sales-portal/domain/pricing/screens/management/types';
import {
  formatCompactCurrency,
  formatCompactNumber,
  formatPercentage,
} from '@sales-portal/blocs/utils/money';
import { formatCurrency } from '@/utils/money';
import { formatSummaryDate, momentWithTimezone } from '@/utils/date';
import { getLocalizedPackUnit } from '@/utils/item';
import { PackUnit } from '@/enums';
import { RecentPriceChangeEvent } from '@sales-portal/domain/pricing/screens/overview/types';
import { resolveDelta } from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';

const MISSING_VALUE = '--';

// Labels match the pricing_impact microproduct's source filter, which "View all"
// navigates into.
const SOURCE_LABEL_KEYS: Record<string, string> = {
  PRICING_AGENT: 'pricing.overview.source.pricing_agent',
  PRICING_MANAGEMENT: 'pricing.overview.source.price_management',
  SALES_REP_TOOLING: 'pricing.overview.source.sales_hub',
};

// An unmapped source is surfaced raw rather than hidden.
export const formatChangeSource = (source: string): string => {
  const key = SOURCE_LABEL_KEYS[source];
  return key ? i18n.t(key) : source;
};

export const formatChangeDate = (updatedAt: string): string =>
  formatSummaryDate(momentWithTimezone(updatedAt));

// The resolver types this as a plain string, so an unrecognized value reads
// neutral rather than being asserted as a decrease.
export const toRecentPriceChangeEvent = (
  event: string,
): RecentPriceChangeEvent => {
  switch (event.toLowerCase()) {
    case 'increase':
      return 'increase';
    case 'decrease':
      return 'decrease';
    default:
      return 'unknown';
  }
};

// Returns lowercase; the UOM cell capitalizes via textTransform.
export const formatChangeUnit = (
  baseUnit: string | null | undefined,
): string =>
  baseUnit ? getLocalizedPackUnit(baseUnit as PackUnit) : MISSING_VALUE;

// --- Feed table: a nullish value renders "--", as in the other pricing tables ---

export const formatHubPrice = (
  amountInMicros: number | null | undefined,
  currencyCode: string,
): string =>
  amountInMicros === null || amountInMicros === undefined
    ? MISSING_VALUE
    : formatCurrency(amountInMicros, currencyCode);

// The GP figure is itself signed, so its own sign drives the arrow — same as
// the pricing_impact event table's GP column.
export const resolveHubImpact = (
  amountInMicros: number | null | undefined,
  currencyCode: string,
): { direction: ArrowDirection | undefined; text: string } => {
  if (amountInMicros === null || amountInMicros === undefined) {
    return { direction: undefined, text: MISSING_VALUE };
  }

  const { direction, formattedValue } = resolveDelta(amountInMicros, (value) =>
    formatCompactCurrency(value, currencyCode),
  );

  return { direction, text: formattedValue };
};

// A price event is never flat — no-op rows are filtered out server-side — so the
// event alone carries the price direction.
export const toEventArrowDirection = (
  event: RecentPriceChangeEvent,
): ArrowDirection | undefined => {
  switch (event) {
    case 'increase':
      return ArrowDirection.Up;
    case 'decrease':
      return ArrowDirection.Down;
    default:
      return undefined;
  }
};

export const formatHubPercentage = (
  percentage: number | null | undefined,
): string =>
  percentage === null || percentage === undefined
    ? MISSING_VALUE
    : formatPercentage(percentage, 1, 1);

// --- Cards: an absent value reads as zero, so a tile always carries a number ---

export const formatBlockCount = (count: number | null | undefined): string =>
  formatCompactNumber(count ?? 0);

export const formatBlockCurrency = (
  amountInMicros: number | null | undefined,
  currencyCode: string,
): string => formatCompactCurrency(amountInMicros ?? 0, currencyCode);

export const resolveWeeklyImpact = (
  amountInMicros: number | null | undefined,
  currencyCode: string,
): { direction: ArrowDirection | undefined; text: string } => {
  const { direction, formattedValue } = resolveDelta(
    amountInMicros ?? 0,
    (value) => formatCompactCurrency(value, currencyCode),
  );

  return {
    direction,
    text: i18n.t('pricing.overview.impact_per_week', { value: formattedValue }),
  };
};
